import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["containerCSSStyles", "children"],
  _excluded2 = ["children", "contentStyles"];
import styled, { useTheme } from 'foundations-theming/utils';
import { AbstractDeviceFrame, StyledDefaultInnerWrapper, StyledDefaultContentWrapper, StyledDefaultOuterWrapper } from './AbstractDeviceFrame';
import { toPx } from '../../../utils/toPx';
import { AbstractDeviceTemplate } from './AbstractDeviceTemplate';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { MERCURY_LAYER } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDesktopFrame = styled.svg.withConfig({
  displayName: "DeviceFrameDesktop__StyledDesktopFrame",
  componentId: "ffc74b-0"
})(({
  $cssStyles
}) => {
  return Object.assign({}, $cssStyles);
});
const DesktopFrame = props => {
  const theme = useTheme();
  const DEVICE_BORDER = theme.color['border-core-default'];
  return /*#__PURE__*/_jsxs(StyledDesktopFrame, Object.assign({}, props, {
    children: [/*#__PURE__*/_jsx("defs", {
      children: /*#__PURE__*/_jsx("path", {
        d: "M0 5.999A6 6 0 0 1 6.003 0h794.994A6 6 0 0 1 807 5.999V547a6 6 0 0 1-6.003 5.999H6.003A6 6 0 0 1 0 547.001V6z",
        id: "a"
      })
    }), /*#__PURE__*/_jsxs("g", {
      transform: "translate(1 1)",
      fill: "none",
      fillRule: "evenodd",
      children: [/*#__PURE__*/_jsx("ellipse", {
        fill: DEVICE_BORDER,
        cx: "21",
        cy: "15.996",
        rx: "4",
        ry: "3.996"
      }), /*#__PURE__*/_jsx("ellipse", {
        fill: DEVICE_BORDER,
        cx: "34",
        cy: "15.996",
        rx: "4",
        ry: "3.996"
      }), /*#__PURE__*/_jsx("ellipse", {
        fill: DEVICE_BORDER,
        cx: "47",
        cy: "15.996",
        rx: "4",
        ry: "3.996"
      })]
    })]
  }));
};
const DESKTOP_DEVICE_SPECS = {
  desktop: {
    Template: DesktopFrame,
    top: 32,
    right: 0,
    bottom: 0,
    left: 0,
    height: 555,
    width: 809
  }
};
const TITLEBAR_HEIGHT = DESKTOP_DEVICE_SPECS.desktop.top;
const COMPUTED_TOOLBAR_HEIGHT = `${TITLEBAR_HEIGHT / DESKTOP_DEVICE_SPECS.desktop.height * 100}%`;
const setToolbarHeight = height => {
  return height ? toPx(TITLEBAR_HEIGHT) : COMPUTED_TOOLBAR_HEIGHT;
};
const DesktopTemplate = ({
  dimensions,
  containerCSSStyles,
  Template,
  children
}) => {
  return /*#__PURE__*/_jsx(AbstractDeviceTemplate, {
    dimensions: dimensions,
    customCSSStyles: {
      position: containerCSSStyles !== null && containerCSSStyles !== void 0 && containerCSSStyles.blockSize ? 'absolute' : undefined /* #6883 */,
      inlineSize: (containerCSSStyles === null || containerCSSStyles === void 0 ? void 0 : containerCSSStyles.blockSize) && toPx(DESKTOP_DEVICE_SPECS.desktop.width)
    },
    Template: Template,
    children: children
  });
};
const DesktopInnerWrapper = ({
  dimensions,
  containerCSSStyles,
  children
}) => {
  return /*#__PURE__*/_jsx(StyledDefaultInnerWrapper, {
    $dimensions: dimensions,
    $cssStyles: {
      borderBottomLeftRadius: 'inherit',
      borderBottomRightRadius: 'inherit',
      marginTop: containerCSSStyles !== null && containerCSSStyles !== void 0 && containerCSSStyles.blockSize ? toPx(TITLEBAR_HEIGHT) : undefined
    },
    children: children
  });
};
const DesktopOuterWrapper = _ref => {
  let {
      containerCSSStyles,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const borderWidth = theme.borderWidth['100'];
  return /*#__PURE__*/_jsx(StyledDefaultOuterWrapper, Object.assign({
    $cssStyles: Object.assign({}, containerCSSStyles, {
      border: `${borderWidth} solid ${theme.color['border-core-default']}`,
      borderRadius: theme.borderRadius['200'],
      blockSize: containerCSSStyles === null || containerCSSStyles === void 0 ? void 0 : containerCSSStyles.blockSize,
      overflow: containerCSSStyles !== null && containerCSSStyles !== void 0 && containerCSSStyles.blockSize ? 'hidden' : undefined,
      position: 'relative',
      '&::before': {
        backgroundColor: theme.color['fill-surface-raised'],
        borderBottom: `${borderWidth} solid ${theme.color['border-core-default']}`,
        borderTopLeftRadius: 'inherit',
        borderTopRightRadius: 'inherit',
        content: "''",
        blockSize: setToolbarHeight(containerCSSStyles === null || containerCSSStyles === void 0 ? void 0 : containerCSSStyles.blockSize),
        left: '0',
        position: 'absolute',
        top: '0',
        width: '100%',
        zIndex: MERCURY_LAYER
      }
    })
  }, rest, {
    children: children
  }));
};
const DesktopContentWrapper = ({
  contentCSSStyles,
  children
}) => {
  return /*#__PURE__*/_jsx(StyledDefaultContentWrapper, {
    $cssStyles: Object.assign({}, contentCSSStyles, {
      borderBottomLeftRadius: 'inherit',
      borderBottomRightRadius: 'inherit'
    }),
    children: children
  });
};
export const DeviceFrameDesktop = _ref2 => {
  let {
      children,
      contentStyles = {}
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    domProps,
    tokenProps: containerStyleProps
  } = filterProps(rest);
  const containerCSSStyles = useMapTokenPropsToCSS(containerStyleProps);
  const {
    tokenProps: contentStyleProps
  } = filterProps(contentStyles);
  const contentCSSStyles = useMapTokenPropsToCSS(contentStyleProps);
  return /*#__PURE__*/_jsx(AbstractDeviceFrame, {
    OuterWrapper: DesktopOuterWrapper,
    containerCSSStyles: containerCSSStyles,
    contentCSSStyles: contentCSSStyles,
    ContentWrapper: DesktopContentWrapper,
    InnerWrapper: DesktopInnerWrapper,
    spec: DESKTOP_DEVICE_SPECS.desktop,
    Template: DesktopTemplate,
    "data-test-id": domProps['data-test-id'],
    children: children
  });
};